import React, { useState } from "react";
import { Button, Icon, TextInput } from "@astryxdesign/core";
import { Eye, EyeClosed } from "lucide-react";
import styles from "./PasswordInput.module.css";

export interface PasswordInputProps {
  /** Accessible label; hidden, so the placeholder carries it visually. */
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** `name` the native form posts this field under. */
  htmlName: string;
  /** Validation message shown under the field. */
  errorMessage?: string | null;
  /** @default false */
  hasAutoFocus?: boolean;
  /** False for a password the form may leave blank, e.g. "keep current". @default true */
  isRequired?: boolean;
}

/**
 * A password field with a show/hide toggle inside its border. Astryx has no
 * password reveal and TextInput has no end slot, so the toggle is overlaid
 * where Astryx draws its own clear button, and the input reserves the room.
 *
 * The error uses the detached variant, which keeps Astryx's in-field status
 * icon out of the toggle's spot. MUST be rendered inside an `AuthLayout`.
 */
const PasswordInput = ({
  label,
  value,
  onChange,
  placeholder,
  htmlName,
  errorMessage = null,
  hasAutoFocus = false,
  isRequired = true,
}: PasswordInputProps): React.ReactElement => {
  const [isRevealed, setIsRevealed] = useState<boolean>(false);

  const status = errorMessage ? { type: "error" as const, message: errorMessage } : undefined;

  return (
    <div className={styles.wrapper}>
      <TextInput
        type={isRevealed ? "text" : "password"}
        label={label}
        value={value}
        onChange={onChange}
        isLabelHidden
        placeholder={placeholder}
        htmlName={htmlName}
        className={styles.input}
        status={status}
        statusVariant="detached"
        hasAutoFocus={hasAutoFocus}
        isRequired={isRequired}
        width="100%"
      />
      <div className={styles.toggle}>
        <Button
          type="button"
          label={isRevealed ? "Hide password" : "Show password"}
          icon={<Icon icon={isRevealed ? Eye : EyeClosed} size="sm" color="secondary" />}
          isIconOnly
          variant="ghost"
          size="sm"
          onClick={() => setIsRevealed(!isRevealed)}
        />
      </div>
    </div>
  );
};

export default PasswordInput;
